import React, { createContext, useContext } from 'react';
import { Outlet } from 'react-router-dom';
import { FEATURE_FLAGS, useFeatureFlagState } from '../../hooks/useFeatureFlag';

// Flag that grants the new (v2) tabbed company profile. The version is driven
// entirely by this flag (managed in Admin > Feature Flags) — there is no in-app
// toggle; the flag being on means v2, off means the original v1 profile.
export const CAN_VIEW_V2_PERMISSION = FEATURE_FLAGS.V2_COMPANY_PROFILE;

interface ProfileVersionState {
  showV2: boolean;
  // Whether the underlying feature flags have resolved yet. Consumers that drive
  // page-wide chrome (the nav shell) can hold render until this is true to avoid
  // flashing the wrong version.
  loaded: boolean;
}

const ProfileVersionContext = createContext<ProfileVersionState>({
  showV2: false,
  loaded: false,
});

export const ProfileVersionProvider = ProfileVersionContext.Provider;

/**
 * Resolves which profile version to show: v2 when the `can_view_v2_company_profile`
 * feature flag is enabled for the viewer (managed in Admin > Feature Flags), v1
 * otherwise. Call this once at the app root and provide it via
 * {@link ProfileVersionProvider}; profile pages and the nav shell read it with
 * {@link useProfileVersion} so they flip between v1 and v2 together.
 */
export function useProfileVersionValue(): ProfileVersionState {
  const { enabled: showV2, loaded } = useFeatureFlagState(CAN_VIEW_V2_PERMISSION);
  return { showV2, loaded };
}

/** Reads the shared profile-version state from context (see {@link ProfileVersionProvider}). */
export function useProfileVersion(): ProfileVersionState {
  return useContext(ProfileVersionContext);
}

/**
 * Layout route that resolves the profile version once and shares it via context
 * with everything it wraps (the nav shell and the profile pages). Mount this
 * inside the authenticated area only — it reads flags via an auth'd request, so
 * mounting it before login would fetch without a session.
 */
export function ProfileVersionLayer() {
  const version = useProfileVersionValue();
  return (
    <ProfileVersionProvider value={version}>
      <Outlet />
    </ProfileVersionProvider>
  );
}
